<template>
  <v-container fluid class="pl-7 pr-6">
    <v-btn
      class="ma-2"
      tile
      outlined
      color="primaryLigth"
      @click="returnToSearch"
    >
      <v-icon left>mdi-chevron-double-left</v-icon>{{ $t("return") }}
    </v-btn>

    <v-sheet elevation="1">
      <!-- Patient Data Table -->
      <v-card elevation="2" class="mb-3" :loading="isStudyLoading">
        <v-data-table
          :headers="headers"
          :items="patientTable"
          disable-sort
          disable-pagination
          disable-filtering
          hide-default-footer
        >
          <template v-slot:[`item.value`]="props">
            <v-edit-dialog
              @save="patientEditDialog()"
              v-if="props.item.tag === 'patName'"
            >
              {{ props.item.value }}
              <template v-slot:input>
                <v-text-field
                  v-model="familyName"
                  hint="Family Name"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                ></v-text-field>
                <v-text-field
                  v-model="givenName"
                  hint="Given Name"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                ></v-text-field>
                <v-text-field
                  v-model="middleName"
                  hint="Middle Name"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                ></v-text-field>
                <v-text-field
                  v-model="namePrefix"
                  hint="Name Prefix"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                ></v-text-field>
                <v-text-field
                  v-model="nameSuffix"
                  hint="Name Suffix"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                  class="input-padding"
                ></v-text-field>
              </template>
            </v-edit-dialog>
            <v-edit-dialog
              :return-value.sync="props.item.value"
              @save="patientSaveEnabled = true"
              v-else
            >
              {{ props.item.value }}
              <template v-slot:input>
                <v-text-field
                  v-model="props.item.value"
                  :hint="props.item.hint"
                  :readonly="props.item.blocked"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                  class="input-padding"
                ></v-text-field>
              </template>
            </v-edit-dialog>
          </template>
        </v-data-table>

        <v-btn
          class="ma-2"
          tile
          outlined
          color="primaryLigth"
          :disabled="!patientSaveEnabled"
          :loading="updatePatientLoading"
          @click="updatePatient"
        >
          <v-icon left>mdi-content-save</v-icon>{{ $t("save") }}
        </v-btn>
        <v-btn
          class="ma-2"
          tile
          outlined
          color="primaryLigth"
          @click="findPatientModal = true"
        >
          <v-icon left>mdi-account-switch</v-icon>{{ $t("find patient") }}
        </v-btn>
      </v-card>

      <!-- Study Data Table -->
      <v-card elevation="2" class="mb-3" :loading="isStudyLoading">
        <v-data-table
          :headers="headers"
          :items="studyTable"
          disable-sort
          disable-pagination
          disable-filtering
          hide-default-footer
        >
          <template v-slot:[`item.value`]="props">
            <v-edit-dialog
              :return-value.sync="props.item.value"
              @save="studySaveEnabled = true"
            >
              <v-btn
                icon
                v-on:click.exact.stop="renewUID(props, false)"
                v-if="props.item.tag === 'studyIuid'"
              >
                <v-icon>mdi-autorenew</v-icon>
              </v-btn>
              {{ props.item.value }}
              <template v-slot:input>
                <v-text-field
                  v-model="props.item.value"
                  :hint="props.item.hint"
                  :readonly="props.item.disabled"
                  :rules="props.item.rules"
                  persistent-hint
                  single-line
                  counter
                ></v-text-field>
              </template>
            </v-edit-dialog>
          </template>
        </v-data-table>

        <v-btn
          class="ma-2"
          tile
          outlined
          color="primaryLigth"
          :disabled="!studySaveEnabled"
          :loading="updateStudyLoading"
          @click="updateStudy"
        >
          <v-icon left>mdi-content-save</v-icon>{{ $t("save") }}
        </v-btn>

        <v-btn
          class="ma-2"
          tile
          outlined
          color="primaryLigth"
          @click="cloneStudyOpenModal"
          v-if="$store.getters.resources.studies.create"
        >
          <v-icon left>mdi-content-copy</v-icon>{{ $t("clone") }}
        </v-btn>

        <v-btn
          class="ma-2"
          tile
          outlined
          color="red"
          :loading="deleteStudyLoading"
          @click="deleteStudyOpenModal"
          v-if="$store.getters.resources.studies.delete"
        >
          <v-icon left>mdi-delete</v-icon>{{ $t("delete") }}
        </v-btn>
      </v-card>

      <!-- Series Data Table -->
      <v-card elevation="2" :loading="isSeriesLoading">
        <v-stepper v-model="seriesStepper" @change="stepperChange" vertical>
          <template v-for="n in seriesSteps">
            <v-stepper-step
              :key="`${n}-step`"
              :step="n"
              edit-icon="undefined"
              editable
            >
              Series N°: {{ studySeries[n - 1].seriesNo }}
              <template v-if="isSeriesDesc(studySeries[n - 1].seriesDesc)"
                >({{ studySeries[n - 1].seriesDesc }})</template
              >
            </v-stepper-step>

            <v-stepper-content :key="`${n}-content`" :step="n">
              <v-row>
                <v-col>
                  <v-data-table
                    :headers="headers"
                    :items="seriesTable"
                    disable-sort
                    disable-pagination
                    disable-filtering
                    hide-default-footer
                  >
                    <template v-slot:[`item.value`]="props">
                      <v-edit-dialog
                        :return-value.sync="props.item.value"
                        @save="seriesSaveEnabled = true"
                      >
                        <v-btn
                          icon
                          v-on:click.exact.stop="renewUID(props, true)"
                          v-if="props.item.tag === 'seriesIuid'"
                        >
                          <v-icon>mdi-autorenew</v-icon>
                        </v-btn>
                        {{ props.item.value }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="props.item.value"
                            :hint="props.item.hint"
                            :readonly="props.item.disabled"
                            :rules="props.item.rules"
                            :type="props.item.type"
                            persistent-hint
                            single-line
                            counter
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
              <v-btn
                class="ma-2"
                tile
                outlined
                color="primaryLigth"
                @click="updateSeries"
                :disabled="!seriesSaveEnabled"
              >
                <v-icon left>mdi-content-save</v-icon>{{ $t("save") }}
              </v-btn>
              <v-btn
                class="ma-2"
                color="red"
                v-if="
                  studySeries.length > 1 &&
                  $store.getters.resources.studies.delete
                "
                @click="deleteSeriesModal = true"
                outlined
                tile
              >
                <v-icon left>mdi-delete</v-icon>{{ $t("delete") }}
              </v-btn>
            </v-stepper-content>
          </template>
        </v-stepper>
      </v-card>

      <!-- Clone Study Modal -->
      <v-dialog v-model="cloneStudyModal" max-width="600px">
        <v-card>
          <v-card-title>{{ $t("Clone Study") }}</v-card-title>
          <v-card-text>
            <v-text-field
              label="New Accession Number"
              v-model="cloneInput"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cloneStudyModal = false">{{
              $t("cancel")
            }}</v-btn>
            <v-btn
              text
              color="primaryLigth"
              :loading="cloneStudyLoading"
              @click="cloneStudy"
              >{{ $t("clone") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Study Modal -->
      <v-dialog v-model="deleteStudyModal" max-width="600px">
        <v-card>
          <v-card-title class="headline">
            {{ $t("Are you sure you want to delete study?") }}
          </v-card-title>
          <v-card-text>
            {{ $t("You could not undo this action.") }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteStudyModal = false">{{
              $t("cancel")
            }}</v-btn>
            <v-btn
              text
              color="red"
              @click="deleteStudy"
              ref="deleteStudyButton"
              >{{ $t("delete") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Series Modal -->
      <v-dialog v-model="deleteSeriesModal" max-width="600px">
        <v-card>
          <v-card-title class="headline">
            {{ $t("Are you sure you want to delete series?") }}
          </v-card-title>
          <v-card-text>
            {{ $t("You could not undo this action.") }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteSeriesModal = false">{{
              $t("cancel")
            }}</v-btn>
            <v-btn
              text
              color="red"
              :loading="deleteSeriesLoading"
              @click="deleteSeries"
              >{{ $t("delete") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Empty Study Modal -->
      <v-dialog v-model="emptyStudyModal" max-width="600px">
        <v-card>
          <v-card-title class="headline">Error</v-card-title>
          <v-card-text>
            {{ $t("Could not find any study.") }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="returnToSearch">{{ $t("accept") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Find Patient -->
      <FindPatient
        v-if="findPatientModal"
        @onClose="findPatientModal = false"
        @onUpdate="updatePatientModal"
      />
    </v-sheet>
  </v-container>
</template>

<script>
import axios from "axios";
import i18n from "../../i18n";
import EventBus from "../../event-bus";

import FindPatient from "./Edit/FindPatient.vue";

export default {
  name: "Edit",
  components: {
    FindPatient,
  },
  props: {
    study: String,
  },
  data: () => ({
    // Loaders
    isStudyLoading: false,
    isSeriesLoading: false,
    deleteStudyLoading: false,
    deleteSeriesLoading: false,
    cloneStudyLoading: false,
    updatePatientLoading: false,
    updateStudyLoading: false,
    // Table
    headers: [
      { text: i18n.t("Name"), value: "name", width: 160 },
      { text: i18n.t("Value"), value: "value" },
    ],
    patientTable: [],
    patientSaveEnabled: false,
    studyTable: [],
    studySaveEnabled: false,
    studyDbId: "",
    seriesTable: [],
    seriesSaveEnabled: false,
    // Stepper
    seriesStepper: 0,
    seriesSteps: 0,
    studySeries: [],
    // Modals
    deleteStudyModal: false,
    deleteSeriesModal: false,
    emptyStudyModal: false,
    findPatientModal: false,
    cloneStudyModal: false,
    cloneInput: null,
    // Patient Name
    familyName: "",
    givenName: "",
    middleName: "",
    namePrefix: "",
    nameSuffix: "",
  }),
  methods: {
    updatePatientModal(src) {
      for (let i = 0; i < this.patientTable.length; i++) {
        if (this.patientTable[i].tag === "patName") {
          this.patientTable[i].value = src.patName;
        }
        if (this.patientTable[i].tag === "patId") {
          this.patientTable[i].value = src.patId;
        }
        if (this.patientTable[i].tag === "patIdIssuer") {
          this.patientTable[i].value = src.patIdIssuer;
        }
        if (this.patientTable[i].tag === "patSex") {
          this.patientTable[i].value = src.patSex;
        }
        if (this.patientTable[i].tag === "patBirthDate") {
          this.patientTable[i].value = src.patBirthDate;
        }
      }

      this.findPatientModal = false;
      this.patientSaveEnabled = true;
    },
    sendError(error) {
      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    patientEditDialog() {
      let value = this.familyName + "^" + this.givenName;
      if (
        this.middleName !== "" ||
        this.namePrefix !== "" ||
        this.nameSuffix !== ""
      ) {
        value +=
          "^" + this.middleName + "^" + this.namePrefix + "^" + this.nameSuffix;
      }

      for (let i = 0; i < this.patientTable.length; i++) {
        if (this.patientTable[i].tag === "patName") {
          this.patientTable[i].value = value;
        }
      }

      this.patientSaveEnabled = true;
    },
    deleteStudyOpenModal() {
      this.deleteStudyModal = true;
      setTimeout(() => this.$refs.deleteStudyButton.$el.focus(), 100);
    },
    isSeriesDesc(value) {
      value !== null && value.length !== 0 ? true : false;
    },
    validateDate(src) {
      if (src.length !== 8) {
        return false;
      }

      let year = Number(src.substring(0, 4));
      if (isNaN(year) || 1000 > year || year > 9999) {
        return false;
      }

      let month = Number(src.substring(4, 6));
      if (isNaN(month) || month < 1 || month > 12) {
        return false;
      }

      let day = Number(src.substring(6, 8));
      if (isNaN(day) || day < 1 || day > 31) {
        return false;
      }

      return true;
    },
    validateTime(src) {
      if (src.length < 6 || src.length > 10) {
        return false;
      }

      let hours = Number(src.substring(0, 2));
      if (isNaN(hours) || hours < 0 || hours > 24) {
        return false;
      }

      let minutes = Number(src.substring(2, 4));
      if (isNaN(minutes) || minutes < 0 || minutes > 60) {
        return false;
      }

      let seconds = Number(src.substring(4, 6));
      if (isNaN(seconds) || seconds < 0 || seconds > 60) {
        return false;
      }

      let millis = Number(src.substring(7, 10));
      if (isNaN(millis) || millis < 0 || millis > 999) {
        return false;
      }

      return true;
    },
    filterStudyTime(src) {
      if (src === '000000.000') {
        return '';
      } else {
        return src;
      }
    },
    getStudy(studyDbId) {
      this.isStudyLoading = true;
      let patientTable;
      let studyTable;

      axios({
        url: "/api/v1/dicom/find/studies",
        method: "POST",
        data: {
          studyDbId: studyDbId,
        },
      })
        .then((response) => {
          this.isStudyLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            if (response.data.studies.length === 1) {
              let study = response.data.studies[0];

              patientTable = new Array();
              studyTable = new Array();

              var addRow = function (src) {
                src.oldValue = src.value;

                if (src.isPatTable) {
                  patientTable.push(src);
                } else {
                  studyTable.push(src);
                }
              };

              addRow({
                isPatTable: true,
                tag: "patDbId",
                name: "Patient DB ID",
                value: study.patDbId,
                hint: i18n.t("Read Only"),
                rules: [],
                disabled: true,
              });
              addRow({
                isPatTable: true,
                tag: "patId",
                name: "Patient ID",
                value: study.patId,
                hint: i18n.t("Max. 64 characters. No spaces."),
                rules: [
                  (v) =>
                    (v.length <= 64 && v.indexOf(" ") === -1) ||
                    i18n.t("Max. 64 characters. No spaces."),
                ],
                disabled: false,
              });

              // Load Patient Name splitted data
              let chunks = study.patName.split("^");
              this.familyName =
                typeof chunks[0] === "undefined" ? "" : chunks[0];
              this.givenName =
                typeof chunks[1] === "undefined" ? "" : chunks[1];
              this.middleName =
                typeof chunks[2] === "undefined" ? "" : chunks[2];
              this.namePrefix =
                typeof chunks[3] === "undefined" ? "" : chunks[3];
              this.nameSuffix =
                typeof chunks[4] === "undefined" ? "" : chunks[4];

              addRow({
                isPatTable: true,
                tag: "patName",
                name: "Patient Name",
                value: study.patName,
                hint: null,
                rules: [
                  (v) =>
                    (v.length <= 64 && v.split("^").length <= 5) ||
                    i18n.t("Max. 64 characters and 4 carets (^)."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: true,
                tag: "patIdIssuer",
                name: "Issuer of Patient ID",
                value: study.patIdIssuer,
                hint: i18n.t("Max. 64 characters. No spaces."),
                rules: [
                  (v) =>
                    (v.length <= 64 && v.indexOf(" ") === -1) ||
                    i18n.t("Max. 64 characters. No spaces."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: true,
                tag: "patBirthDate",
                name: "Patient BirthDate",
                value: study.patBirthDate,
                hint: i18n.t("Date format YYYYMMDD."),
                rules: [
                  (v) =>
                    this.validateDate(v) || i18n.t("Date format YYYYMMDD."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: true,
                tag: "patSex",
                name: "Patient Sex",
                value: study.patSex,
                hint: i18n.t("M (Male), F (Female) or O (Other) only."),
                rules: [
                  (v) =>
                    v === "M" ||
                    v === "F" ||
                    v === "O" ||
                    i18n.t("M (Male), F (Female) or O (Other) only."),
                ],
                disabled: false,
              });

              addRow({
                isPatTable: false,
                tag: "studyDbId",
                name: "Study DB ID",
                value: study.studyDbId,
                hint: i18n.t("Read Only"),
                rules: [],
                disabled: true,
              });
              addRow({
                isPatTable: false,
                tag: "studyIuid",
                name: "Study Instance UID",
                value: study.studyIuid,
                hint: i18n.t("Max. 64 characters. No spaces."),
                rules: [
                  (v) =>
                    (v.length <= 64 && v.indexOf(" ") === -1) ||
                    i18n.t("Max. 64 characters. No spaces."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: false,
                tag: "studyId",
                name: "Study ID",
                value: study.studyId,
                hint: i18n.t("Max. 16 characters. No spaces."),
                rules: [
                  (v) =>
                    (v.length <= 16 && v.indexOf(" ") === -1) ||
                    i18n.t("Max. 16 characters. No spaces."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: false,
                tag: "accessionNo",
                name: "Accession Number",
                value: study.accessionNo,
                hint: i18n.t("Max. 16 characters. No spaces."),
                rules: [
                  (v) =>
                    (v.length <= 16 && v.indexOf(" ") === -1) ||
                    i18n.t("Max. 16 characters. No spaces."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: false,
                tag: "studyDate",
                name: "Study Date",
                value: study.studyDate,
                hint: i18n.t("Date format YYYYMMDD."),
                rules: [
                  (v) =>
                    this.validateDate(v) || i18n.t("Date format YYYYMMDD."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: false,
                tag: "studyTime",
                name: "Study Time",
                value: this.filterStudyTime(study.studyTime),
                hint: i18n.t("Time format HHMMSS.fff."),
                rules: [
                  (v) =>
                    this.validateTime(v) || i18n.t("Time format HHMMSS.fff."),
                ],
                disabled: false,
              });
              addRow({
                isPatTable: false,
                tag: "studyDesc",
                name: "Study Description",
                value: study.studyDesc,
                hint: i18n.t("Max. 64 characters."),
                rules: [(v) => v.length <= 64 || i18n.t("Max. 64 characters.")],
                disabled: false,
              });
              addRow({
                isPatTable: false,
                tag: "aeTitle",
                name: "AE-Title",
                value: study.aeTitle,
                hint: i18n.t("Read Only"),
                rules: [],
                disabled: true,
              });

              this.studyDbId = study.studyDbId;
              this.patientTable = patientTable;
              this.studyTable = studyTable;
            } else if (response.data.studies.length === 0) {
              this.emptyStudyModal = true;
            } else {
              this.sendError(
                i18n.t("Two or more studies with same StudyDbId!")
              );
            }
          }
        })
        .catch((error) => {
          this.isStudyLoading = false;
          this.sendError(error);
        });
    },
    getSeries(studyDbId) {
      this.isSeriesLoading = true;

      axios({
        url: "/api/v1/dicom/find/series",
        method: "POST",
        data: {
          studyDbId: studyDbId,
        },
      })
        .then((response) => {
          this.isSeriesLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.seriesSteps = response.data.series.length;
            this.studySeries = response.data.series;
          }
        })
        .catch((error) => {
          this.isSeriesLoading = false;
          this.sendError(error);
        });
    },
    renewUID(src, isSeries) {
      if (
        src.item.value.charAt(0) !== undefined &&
        src.item.value.charAt(0) !== "*"
      ) {
        src.item.value = "*" + src.item.value;
      }
      if (isSeries) {
        this.seriesSaveEnabled = true;
      } else {
        this.studySaveEnabled = true;
      }
    },
    stepperChange() {
      let series = this.studySeries[this.seriesStepper - 1];
      let seriesTable = new Array();

      function addRow(src) {
        seriesTable.push({
          tag: src.tag,
          name: src.name,
          value: src.value,
          oldValue: src.value,
          hint: src.hint,
          rules: src.rules,
          disabled: src.disabled,
          changed: false,
          type: src.type,
        });
      }

      addRow({
        tag: "seriesDbId",
        name: "Series DB ID",
        value: series.seriesDbId,
        hint: i18n.t("Read Only"),
        rules: [],
        disabled: true,
        type: "text",
      });
      addRow({
        tag: "seriesIuid",
        name: "Series Instance UID",
        value: series.seriesIuid,
        hint: i18n.t("Max. 64 characters. No spaces."),
        rules: [
          (v) =>
            (v.length <= 64 && v.indexOf(" ") === -1) ||
            i18n.t("Max. 64 characters. No spaces."),
        ],
        disabled: false,
        type: "text",
      });
      addRow({
        tag: "seriesNo",
        name: "Series No",
        value: Number(series.seriesNo),
        hint: i18n.t("Integer Only."),
        rules: [(v) => Number.isInteger(Number(v)) || i18n.t("Integer Only.")],
        disabled: false,
        type: "number",
      });
      addRow({
        tag: "seriesDate",
        name: "Series Date",
        value: series.seriesDate,
        hint: i18n.t("Date format YYYYMMDD."),
        rules: [(v) => this.validateDate(v) || i18n.t("Date format YYYYMMDD.")],
        disabled: false,
        type: "text",
      });
      addRow({
        tag: "seriesTime",
        name: "Series Time",
        value: series.seriesTime,
        hint: i18n.t("Time format HHMMSS.fff."),
        rules: [
          (v) => this.validateTime(v) || i18n.t("Time format HHMMSS.fff."),
        ],
        disabled: false,
        type: "text",
      });
      addRow({
        isPatTable: false,
        tag: "seriesDesc",
        name: "Series Description",
        value: series.seriesDesc,
        hint: i18n.t("Read Only"),
        rules: [],
        disabled: true,
      });
      addRow({
        isPatTable: false,
        tag: "instName",
        name: "Institution Name",
        value: series.instName,
        hint: i18n.t("Read Only"),
        rules: [],
        disabled: true,
      });
      addRow({
        isPatTable: false,
        tag: "stationName",
        name: "Station Name",
        value: series.stationName,
        hint: i18n.t("Read Only"),
        rules: [],
        disabled: true,
      });
      addRow({
        isPatTable: false,
        tag: "bodyPart",
        name: "Body Part",
        value: series.bodyPart,
        hint: i18n.t("Read Only"),
        rules: [],
        disabled: true,
      });

      this.seriesTable = seriesTable;
    },
    deleteStudy() {
      this.deleteStudyLoading = true;

      axios({
        url: "/api/v1/dicom/study/" + this.studyDbId,
        method: "DELETE",
      })
        .then((response) => {
          this.deleteStudyLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.$emit("returnToSearch");
          }
        })
        .catch((error) => {
          this.deleteStudyLoading = false;
          this.sendError(error);
        });
    },
    deleteSeries() {
      this.deleteSeriesLoading = true;
      let seriesDbId = this.studySeries[this.seriesStepper - 1].seriesDbId;

      axios({
        url: "/api/v1/dicom/series/" + seriesDbId,
        method: "DELETE",
      })
        .then((response) => {
          this.deleteSeriesLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.deleteSeriesModal = false;
            this.getStudy(this.study);
            this.getSeries(this.study);
          }
        })
        .catch((error) => {
          this.deleteSeriesLoading = false;
          this.sendError(error);
        });
    },
    updatePatient() {
      this.updatePatientLoading = true;
      let update_query = new Object();

      this.patientTable.forEach((element) => {
        if (element.value !== element.oldValue) {
          update_query[element.tag] = element.value;
        }
      });

      axios({
        url: "/api/v1/dicom/studies/" + this.studyDbId,
        method: "PUT",
        data: update_query,
      })
        .then((resp) => {
          this.updatePatientLoading = false;

          if (
            !resp.data ||
            !resp.data.status ||
            !(resp.data.status == "done")
          ) {
            this.sendError(resp.data.message);
            this.$store.dispatch("logError", {
              title: "Update patient data.",
              desc: resp.data.message,
            });
          } else {
            this.returnToSearch();
          }
        })
        .catch((error) => {
          this.updatePatientLoading = false;
          this.sendError(error);
        });
    },
    updateStudy() {
      this.updateStudyLoading = true;
      let update_query = new Object();

      this.studyTable.forEach((element) => {
        if (element.value !== element.oldValue) {
          update_query[element.tag] = element.value;
        }
      });

      axios({
        url: "/api/v1/dicom/studies/" + this.studyDbId,
        method: "PUT",
        data: update_query,
      })
        .then((resp) => {
          this.updateStudyLoading = false;

          if (
            !resp.data ||
            !resp.data.status ||
            !(resp.data.status == "done")
          ) {
            this.sendError(resp.data.message);
            this.$store.dispatch("logError", {
              title: "Update study data.",
              desc: resp.data.message,
            });
          } else {
            this.returnToSearch();
          }
        })
        .catch((error) => {
          this.updateStudyLoading = false;
          this.sendError(error);
        });
    },
    updateSeries() {
      this.updateSeries = true;
      let update_query = new Object();

      this.seriesTable.forEach((element) => {
        if (element.value !== element.oldValue) {
          update_query[element.tag] = element.value;
        }
      });

      // Force seriesNo into number
      if (typeof update_query["seriesNo"] !== "undefined") {
        update_query["seriesNo"] = Number(update_query["seriesNo"]);
      }

      axios({
        url: "/api/v1/dicom/series/" + this.seriesTable[0].value,
        method: "PUT",
        data: update_query,
      })
        .then((resp) => {
          this.updateSeriesLoading = false;

          if (
            !resp.data ||
            !resp.data.status ||
            !(resp.data.status == "done")
          ) {
            this.sendError(resp.data.message);
            this.$store.dispatch("logError", {
              title: "Update series data.",
              desc: resp.data.message,
            });
          } else {
            this.returnToSearch();
          }
        })
        .catch((error) => {
          this.updateSeriesLoading = false;
          this.sendError(error);
        });
    },
    cloneStudyOpenModal() {
      this.cloneInput = "";
      this.cloneStudyModal = true;
    },
    cloneStudy() {
      this.cloneStudyLoading = true;

      axios({
        url: "/api/v1/dicom/clone/" + this.studyDbId,
        method: "POST",
        data: {
          accessionNo: this.cloneInput,
        },
      })
        .then((response) => {
          this.cloneStudyLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.cloneStudyModal = false;
          }
        })
        .catch((error) => {
          this.cloneStudyLoading = false;
          this.sendError(error);
        });
    },
    returnToSearch() {
      this.$emit("returnToSearch");
    },
  },
  mounted() {
    this.getStudy(this.study);
    this.getSeries(this.study);
  },
};
</script>

<style scoped>
.v-sheet {
  width: 100%;
}
.input-padding {
  padding-bottom: 16px;
}
</style>